/**
 * `/admin/vendors` and `/admin/categories` - spec 09's partner surfaces.
 *
 * FR-8.6 is the point of every mutation here: `publish` puts a partner in the
 * member directory on the member's NEXT request, with no app store release and
 * nothing redeployed. `status` and `deleted_at` are what the member-visible
 * predicate reads (`vendor_service.visible_to_members`).
 *
 * Soft delete only (FR-6.1.5): `delete` sets `deleted_at` and keeps the row, so
 * the partner's contact history survives. That is also why the logo endpoint
 * exists separately - an archived partner keeps its mark.
 */

import { request } from "./client";
import type { DiscountType, Page, VendorStatus } from "./types";

export type Vendor = {
  id: string;
  name: string;
  slug: string | null;
  logo_url: string | null;
  short_description: string | null;
  description: string | null;
  category_id: string | null;
  category_name: string | null;
  phone: string | null;
  email: string | null;
  website_url: string | null;
  address: string | null;
  map_url: string | null;
  latitude: string | null;
  longitude: string | null;
  discount_type: DiscountType | null;
  discount_value: string | null;
  /** Server-rendered ("5% OFF"), so the panel never re-formats a discount. */
  discount_label: string | null;
  discount_terms: string | null;
  valid_from: string | null;
  valid_to: string | null;
  status: VendorStatus;
  is_featured: boolean;
  display_order: number;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
  /** Spec 21. Up to three, in slot order; only the photo endpoints change them. */
  photos: VendorPhoto[];
};

/** `slot` (0-2) is the position in the member app's banner. */
export type VendorPhoto = { slot: number; url: string };

export type Category = {
  id: string;
  name: string;
  slug: string;
  display_order: number;
  is_active: boolean;
  created_at: string;
  /** How many partners use it - the count `DELETE` refuses on when non-zero. */
  vendor_count: number;
};

export type VendorWrite = {
  name: string;
  slug?: string;
  short_description?: string;
  description?: string;
  category_id?: string | null;
  phone?: string;
  email?: string;
  website_url?: string;
  address?: string;
  map_url?: string;
  latitude?: string;
  longitude?: string;
  discount_type?: DiscountType;
  discount_value?: string;
  discount_terms?: string;
  valid_from?: string;
  valid_to?: string;
  status?: VendorStatus;
  is_featured?: boolean;
  display_order?: number;
};

export type VendorOutcome = { vendor: Vendor; message: string };
export type CategoryOutcome = { category: Category; message: string };
export type MessageOutcome = { message: string };

export type VendorFilters = {
  q?: string;
  status?: VendorStatus;
  category_id?: string;
  include_deleted?: boolean;
  limit?: number;
  offset?: number;
};

export function fetchVendors(filters: VendorFilters): Promise<Page<Vendor>> {
  return request<Page<Vendor>>("/admin/vendors", { query: { ...filters } });
}

export function fetchVendor(vendorId: string): Promise<VendorOutcome> {
  return request<VendorOutcome>(`/admin/vendors/${vendorId}`);
}

export function createVendor(payload: VendorWrite): Promise<VendorOutcome> {
  return request<VendorOutcome>("/admin/vendors", { method: "POST", body: payload });
}

export function updateVendor(vendorId: string, payload: Partial<VendorWrite>): Promise<VendorOutcome> {
  return request<VendorOutcome>(`/admin/vendors/${vendorId}`, { method: "PATCH", body: payload });
}

/** FR-6.1.5 - soft delete: `deleted_at` set, row and history kept. */
export function deleteVendor(vendorId: string): Promise<MessageOutcome> {
  return request<MessageOutcome>(`/admin/vendors/${vendorId}`, { method: "DELETE" });
}

export function publishVendor(vendorId: string): Promise<VendorOutcome> {
  return request<VendorOutcome>(`/admin/vendors/${vendorId}/publish`, { method: "POST" });
}

export function unpublishVendor(vendorId: string): Promise<VendorOutcome> {
  return request<VendorOutcome>(`/admin/vendors/${vendorId}/unpublish`, { method: "POST" });
}

export function archiveVendor(vendorId: string): Promise<VendorOutcome> {
  return request<VendorOutcome>(`/admin/vendors/${vendorId}/archive`, { method: "POST" });
}

export type ReorderOutcome = { updated: number; message: string };

/** One audit row for the whole list, not one per partner. */
export function reorderVendors(items: Array<{ id: string; display_order: number }>): Promise<ReorderOutcome> {
  return request<ReorderOutcome>("/admin/vendors/order", {
    method: "PUT",
    body: { items },
  });
}

/**
 * `POST /admin/vendors/{id}/logo` - multipart, and the one place this client
 * sends something that is not JSON. The API validates the type from the file's
 * BYTES, not its `Content-Type` (NFR-10.3.7), so the browser's guess is not
 * trusted anywhere in the path.
 */
export async function uploadVendorLogo(vendorId: string, file: File): Promise<{ logo_url: string; message: string }> {
  const body = new FormData();
  body.append("logo", file);
  // `request` sets Content-Type for JSON bodies only; for FormData the browser
  // must set it itself so the multipart boundary is included.
  return request(`/admin/vendors/${vendorId}/logo`, { method: "POST", body });
}

/**
 * `PUT /admin/vendors/{id}/photos/{slot}` - spec 21. Send the output of
 * `optimizePhoto`, never the raw file: the API's limit is 2 MB, and the crop
 * is what makes this panel's preview match the member banner.
 */
export async function uploadVendorPhoto(
  vendorId: string,
  slot: number,
  photo: Blob,
  fileName: string,
): Promise<{ photos: VendorPhoto[]; message: string }> {
  const body = new FormData();
  body.append("photo", photo, fileName);
  return request(`/admin/vendors/${vendorId}/photos/${slot}`, { method: "PUT", body });
}

export function removeVendorPhoto(
  vendorId: string,
  slot: number,
): Promise<{ photos: VendorPhoto[]; message: string }> {
  return request(`/admin/vendors/${vendorId}/photos/${slot}`, { method: "DELETE" });
}

export function fetchCategories(): Promise<{ items: Category[]; total: number }> {
  return request<{ items: Category[]; total: number }>("/admin/categories");
}

export function createCategory(payload: {
  name: string;
  slug?: string;
  display_order?: number;
  is_active?: boolean;
}): Promise<CategoryOutcome> {
  return request<CategoryOutcome>("/admin/categories", { method: "POST", body: payload });
}

export function updateCategory(
  categoryId: string,
  payload: Partial<{ name: string; slug: string; display_order: number; is_active: boolean }>,
): Promise<CategoryOutcome> {
  return request<CategoryOutcome>(`/admin/categories/${categoryId}`, {
    method: "PATCH",
    body: payload,
  });
}

/** Refused with 409 `category_in_use` while any partner still uses it. */
export function deleteCategory(categoryId: string): Promise<MessageOutcome> {
  return request<MessageOutcome>(`/admin/categories/${categoryId}`, { method: "DELETE" });
}
